import React, { useEffect, useMemo, useState } from "react";
import {
  Button, Drawer, Form, Input, InputNumber, Select, DatePicker, Switch,
  Upload, message, Popconfirm, Empty, Spin, Tooltip,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  FileTextOutlined, CheckOutlined, SafetyCertificateOutlined, EyeOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { registerServices } from "../../../services/contractorRegister.service";
import { attachment } from "../../../services/attachment.service";
import { visitorInductionService } from "@iso/services";
import { EXPIRY_META, STATUS_PILL, SITE_ACCESS_META, SITE_ACCESS_REASON } from "@iso/constants/contractorRegisters";
import RunInductionModal from "@iso/containers/Pages/Visitor/RunInductionModal";
import ViewSurveyResultModal from "../Form/ViewSurveyResultModal";

const { TextArea } = Input;

const DATE_FIELDS = new Set([
  "start_date", "expiry_date", "last_service_date", "next_service_date",
  "issue_date", "completion_date", "review_date", "last_on_site",
]);
const FILE_FIELDS = new Set([
  "document_attachment_id", "rego_attachment_id", "prestart_attachment_id",
  "risk_assessment_attachment_id", "front_attachment_id", "back_attachment_id",
]);

const fmtDate = (v) => (v ? moment(v).format("DD MMM YYYY") : "—");
const fmtCurrency = (v) =>
  v == null ? "—" : "$" + Number(v).toLocaleString("en-AU", { maximumFractionDigits: 0 });
const optionLabel = (options, value) =>
  options?.find((o) => o.value === value)?.label || value || "—";

/**
 * Generic compliance-register tab: table from config.columns, a
 * create/edit drawer from config.fields, delete with confirm. Shared by
 * all six registers on the contractor profile. Owner/admin-gated at the
 * button level; the API enforces it regardless.
 *
 * `onChanged` fires after any successful write so the parent can refresh
 * the state a register row feeds — the header compliance counts, and the
 * worker pool behind the Licences/Inductions holder selectors.
 *
 * `rowActions` is an optional (row, refresh) => node for actions that belong to
 * ONE register rather than all six — Personnel's grant/revoke login, today. It
 * renders before Edit/Delete and is handed `refresh` so the action can re-read
 * the table without the parent having to reach in.
 */
const RegisterTab = ({ config, contractorId, propertyId, canManage, workerOptions = [], onChanged, rowActions }) => {
  // Two ways in, deliberately:
  //  - the WORKER register offers a per-row shortcut, because you are already
  //    looking at the person;
  //  - the INDUCTION register offers a header button, because that is where the
  //    two ways of getting an induction record sit together (Record Induction
  //    is the manual attestation, Run Induction is an actual form). It has to
  //    ask who first, which is what the picker drawer is for.
  const [inductionWorker, setInductionWorker] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedWorkerId, setPickedWorkerId] = useState(undefined);
  const isWorkerRegister = config.endpoint === "contractor-worker";
  const isInductionRegister = config.endpoint === "contractor-induction";
  const supportsInduction = isWorkerRegister || isInductionRegister;

  // The answers behind a mirrored induction, in the shape ViewSurveyResultModal
  // expects (see FormModal::showSchemaSubmission — same contract, so induction
  // answers render exactly like every other submission).
  const [inductionResult, setInductionResult] = useState(null);
  const [openingSubmission, setOpeningSubmission] = useState(null);

  /**
   * Open the answers behind an induction that was actually completed here.
   *
   * Only a mirrored row has any: a hand-entered attestation records that an
   * induction happened somewhere else, and its evidence is the attached
   * certificate, not a form. `is_system_recorded` is exactly that distinction,
   * which is why the button is gated on it rather than on the link column.
   *
   * The submission carries the schema snapshot taken when it was submitted, so
   * a record made against an older version of the form still renders its own
   * field set rather than today's.
   */
  const viewInductionAnswers = (row) => {
    setOpeningSubmission(row.id);
    visitorInductionService
      .getSubmission(row.visitor_induction_id)
      .then((res) => {
        if (!res || !res.success) {
          message.error((res && res.message) || "Could not load the induction answers");
          return;
        }
        const sub = res.data.submission;
        setInductionResult({
          form: {
            name: row.induction_type,
            schema_json: sub.schema_json,
          },
          user: sub.submitter_name || row.worker_name || "Worker",
          agree_date: sub.submitted_at,
          global_form_submission: [
            {
              ...(sub.answers || {}),
              submisison_date: sub.submitted_at,
              _schema: sub.schema_json || null,
            },
          ],
        });
      })
      .catch(() => message.error("Could not load the induction answers"))
      .finally(() => setOpeningSubmission(null));
  };

  const startFromPicker = () => {
    const worker = workerOptions.find((w) => w.id === pickedWorkerId);
    if (!worker) {
      message.warning("Choose who this induction is for");
      return;
    }
    setPickerOpen(false);
    setInductionWorker(worker);
  };
  const svc = registerServices[config.endpoint];
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Uploaded attachments keyed by field name: { [field]: {id, name, url} }
  const [attachments, setAttachments] = useState({});
  const [uploadingField, setUploadingField] = useState(null);
  // Re-render the drawer when a switch that gates a field toggles.
  const [formTick, setFormTick] = useState(0);

  const fetchRows = () => {
    setLoading(true);
    svc
      .list(contractorId)
      .then((res) => {
        if (res?.code === "0000") setRows(res.items || []);
        else message.error(res?.message || "Failed to load records");
        setLoading(false);
      })
      .catch((err) => {
        console.error(`Failed to load ${config.endpoint}:`, err);
        message.error("Failed to load records");
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.endpoint, contractorId]);

  const openCreate = () => {
    setEditing(null);
    setAttachments({});
    form.resetFields();
    setDrawerOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    // Seed date pickers as moments, and rehydrate attachment chips from
    // the expanded *_attachment objects the API returns.
    const values = {};
    const atts = {};
    config.fields.forEach((f) => {
      // `valueKey` lets a field seed from a DIFFERENT key than it saves to. Site
      // Access saves `access_status` but must show `site_access`, the derived
      // state the table shows — otherwise the drawer can say Pending about
      // somebody the table (correctly) calls Approved, which is exactly what it
      // used to do.
      const raw = row[f.valueKey || f.name];
      if (DATE_FIELDS.has(f.name)) {
        values[f.name] = raw ? moment(raw) : null;
      } else if (FILE_FIELDS.has(f.name)) {
        values[f.name] = raw ?? null;
        const attKey = f.name.replace(/_id$/, "");
        const att = row[attKey];
        if (att) atts[f.name] = { id: att.id, name: att.display_name || "Document", url: att.full_path };
      } else {
        values[f.name] = raw;
      }
    });
    setAttachments(atts);
    setDrawerOpen(true);
    // Fields mount with the drawer; set after open.
    setTimeout(() => form.setFieldsValue(values), 0);
  };

  const handleUpload = (fieldName) => ({ file, onSuccess, onError }) => {
    setUploadingField(fieldName);
    attachment
      .uploadFile(file, "file")
      .then((res) => {
        const att = res?.attachment;
        if (att?.id) {
          setAttachments((a) => ({
            ...a,
            [fieldName]: { id: att.id, name: att.display_name || file.name, url: att.full_path },
          }));
          form.setFieldsValue({ [fieldName]: att.id });
          onSuccess(res);
        } else {
          message.error("Upload failed");
          onError(new Error("no attachment"));
        }
      })
      .catch((err) => {
        console.error("Upload failed:", err);
        message.error("Failed to upload document");
        onError(err);
      })
      .finally(() => setUploadingField(null));
  };

  const submit = () => {
    form
      .validateFields()
      .then((values) => {
        setSaving(true);
        const item = { ...values };
        Object.keys(item).forEach((k) => {
          if (DATE_FIELDS.has(k)) {
            item[k] = item[k] ? moment(item[k]).format("YYYY-MM-DD") : null;
          }
          if (FILE_FIELDS.has(k)) {
            item[k] = attachments[k]?.id ?? item[k] ?? null;
          }
        });
        const req = editing ? svc.update(editing.id, item) : svc.create(contractorId, item);
        req
          .then((res) => {
            if (res?.code === "0000") {
              message.success(editing ? "Record updated" : "Record added");
              setDrawerOpen(false);
              fetchRows();
              if (onChanged) onChanged();
            } else {
              message.error(res?.message || "Failed to save");
            }
          })
          .catch((err) => {
            console.error("Save failed:", err);
            message.error("Failed to save");
          })
          .finally(() => setSaving(false));
      })
      .catch(() => {});
  };

  const remove = (row) => {
    svc
      .destroy(row.id)
      .then((res) => {
        if (res?.code === "0000") {
          message.success("Record deleted");
          fetchRows();
          if (onChanged) onChanged();
        } else {
          message.error(res?.message || "Failed to delete");
        }
      })
      .catch((err) => {
        console.error("Delete failed:", err);
        message.error("Failed to delete");
      });
  };

  /* ---------------------------- cell render --------------------------- */

  const renderExpiry = (row, col) => {
    if (col.noExpiryKey && row[col.noExpiryKey]) {
      return <span className="reg-muted">No expiry</span>;
    }
    const status = row[col.expiryKey || "expiry_status"];
    const meta = EXPIRY_META[status];
    return (
      <span>
        {fmtDate(row[col.key])}{" "}
        {meta && <span className={`expiry-pill ${meta.className}`}>{meta.label}</span>}
      </span>
    );
  };

  const renderCell = (row, col) => {
    const v = row[col.key];
    switch (col.type) {
      case "text": {
        const subVal = col.sub ? row[col.sub] : null;
        const joinVal = col.joinSub ? row[col.joinSub] : null;
        const main = [v, joinVal].filter(Boolean).join(" ") || "—";
        return (
          <div>
            <div className="reg-main">{main}</div>
            {subVal && <div className="reg-sub">{subVal}</div>}
          </div>
        );
      }
      case "option":
        return optionLabel(col.options, v);
      case "currency":
        return fmtCurrency(v);
      case "date":
        return fmtDate(v);
      case "expiry":
        return renderExpiry(row, col);
      case "bool":
        return v ? <CheckOutlined style={{ color: "#067647" }} /> : <span className="reg-muted">—</span>;
      // Provenance of an induction record. "Recorded manually" is the older,
      // and still perfectly valid, way in — an induction delivered off-site
      // with a certificate attached.
      case "inductionSource": {
        if (!v) {
          return <span className="reg-muted">Recorded manually</span>;
        }
        // A mirrored induction carries its verification with it. A refused one
        // must NOT read as a held induction sitting in the compliance register,
        // and one nobody has looked at yet must not read as accepted.
        // verification_status is null on rows that predate verification — those
        // were accepted under the old rules and stay green.
        if (row.verification_status === "rejected") {
          return <span className="status-pill-sm pill-red">Rejected</span>;
        }
        if (row.verification_status === "unverified") {
          return <span className="status-pill-sm pill-amber">Awaiting verification</span>;
        }
        return <span className="status-pill-sm pill-green">On-site induction</span>;
      }
      case "status": {
        if (!v) return "—";
        const cls = STATUS_PILL[v] || "pill-blue";
        return <span className={`status-pill-sm ${cls}`}>{v.replace(/_/g, " ")}</span>;
      }
      case "worker":
        return row.worker_name || <span className="reg-muted">All workers</span>;
      // The merged Site Access state. Approved is derived from whether the person
      // can actually sign in, so the pill cannot claim access that does not exist;
      // the tooltip adds WHY a row is Pending, which is what somebody needs in
      // order to act on it.
      case "siteAccess": {
        const meta = SITE_ACCESS_META[v] || SITE_ACCESS_META.pending;
        const why =
          v === "pending" ? SITE_ACCESS_REASON[row.login_access] : null;
        return (
          <Tooltip title={why ? `${meta.hint} ${why}` : meta.hint}>
            <span className={`status-pill-sm ${meta.className}`}>{meta.label}</span>
          </Tooltip>
        );
      }
      default:
        return v ?? "—";
    }
  };

  /* ---------------------------- form fields --------------------------- */

  const isFieldVisible = (f) => {
    if (!f.showWhen) return true;
    return f.showWhen(form.getFieldsValue());
  };

  const renderField = (f) => {
    if (!isFieldVisible(f)) return null;
    const rules = f.required
      ? [{ required: true, whitespace: f.type === "text", message: `${f.label} is required` }]
      : [];

    let control;
    switch (f.type) {
      case "textarea":
        control = <TextArea rows={3} placeholder={f.label} />;
        break;
      case "number":
        control = <InputNumber style={{ width: "100%" }} placeholder={f.label} />;
        break;
      case "select":
        control = (
          <Select placeholder={`Select ${f.label.toLowerCase()}`} allowClear showSearch optionFilterProp="children">
            {f.options.map((o) => (
              <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>
            ))}
          </Select>
        );
        break;
      case "worker":
        control = (
          <Select placeholder="Select worker" allowClear showSearch optionFilterProp="children">
            {workerOptions.map((w) => (
              <Select.Option key={w.id} value={w.id}>{w.full_name}</Select.Option>
            ))}
          </Select>
        );
        break;
      case "date":
        control = <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />;
        break;
      case "switch":
        control = <Switch />;
        break;
      case "file": {
        const att = attachments[f.name];
        return (
          <Form.Item key={f.name} label={f.label} className="reg-form-item">
            <Form.Item name={f.name} noStyle><Input type="hidden" /></Form.Item>
            {att ? (
              <div className="reg-file-chip">
                <FileTextOutlined />
                {att.url ? (
                  <a href={att.url} target="_blank" rel="noopener noreferrer">{att.name}</a>
                ) : (
                  <span>{att.name}</span>
                )}
                <Button size="small" type="text" icon={<DeleteOutlined />}
                  onClick={() => { setAttachments((a) => { const n = { ...a }; delete n[f.name]; return n; }); form.setFieldsValue({ [f.name]: null }); }} />
              </div>
            ) : (
              <Upload customRequest={handleUpload(f.name)} showUploadList={false} maxCount={1}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                beforeUpload={(file) => {
                  if (file.size / 1024 / 1024 > 20) { message.error("File must be under 20 MB"); return Upload.LIST_IGNORE; }
                  return true;
                }}>
                <Button icon={<UploadOutlined />} loading={uploadingField === f.name}>Upload</Button>
              </Upload>
            )}
          </Form.Item>
        );
      }
      default:
        control = <Input placeholder={f.label} />;
    }

    return (
      <Form.Item
        key={f.name}
        name={f.name}
        label={f.label}
        rules={rules}
        valuePropName={f.type === "switch" ? "checked" : "value"}
        className="reg-form-item"
      >
        {control}
      </Form.Item>
    );
  };

  const visibleFields = useMemo(
    () => config.fields,
    // formTick forces re-eval of showWhen after a gating switch toggles
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config.fields, formTick]
  );

  return (
    <div className="register-tab">
      <div className="register-head">
        <div className="register-title">{config.title}</div>
        {canManage && (
          <div style={{ display: "flex", gap: 8 }}>
            <Button icon={<PlusOutlined />} className="btn-dark-green" onClick={openCreate}>
              {config.addLabel}
            </Button>
            {isInductionRegister && (
              <Button
                className="btn-outline-green"
                icon={<SafetyCertificateOutlined />}
                onClick={() => {
                  setPickedWorkerId(undefined);
                  setPickerOpen(true);
                }}
              >
                Run Induction
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
      ) : rows.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={config.empty} style={{ padding: "40px 0" }} />
      ) : (
        <div className="register-table-wrap">
          <table className="register-table">
            <thead>
              <tr>
                {config.columns.map((c) => <th key={c.key}>{c.label}</th>)}
                {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  {config.columns.map((c) => <td key={c.key}>{renderCell(row, c)}</td>)}
                  {canManage && (
                    <td>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", alignItems: "center" }}>
                        {isWorkerRegister && (
                          <Tooltip title="Run or send an induction for this worker">
                            <Button
                              size="small"
                              type="text"
                              icon={<SafetyCertificateOutlined />}
                              onClick={() => setInductionWorker(row)}
                            />
                          </Tooltip>
                        )}
                        {/* Only a row the system mirrored has answers behind it;
                            a manual attestation has an attachment instead, and
                            offering View on one would open an empty record. */}
                        {isInductionRegister && row.is_system_recorded && (
                          <Tooltip title="View the submitted answers">
                            <Button
                              size="small"
                              type="text"
                              icon={<EyeOutlined />}
                              loading={openingSubmission === row.id}
                              onClick={() => viewInductionAnswers(row)}
                            />
                          </Tooltip>
                        )}

                        {rowActions ? rowActions(row, fetchRows) : null}

                        <Tooltip title="Edit">
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)} />
                        </Tooltip>
                        <Popconfirm title="Delete this record?" okText="Delete" cancelText="Cancel"
                          onConfirm={() => remove(row)}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Who is this induction for? Asked only from the Inductions register,
          where there is no row to infer it from. A right-side panel rather than
          a modal because the register's own add/edit already uses one, and
          because the induction form that follows needs the width. */}
      <Drawer
        title="Run Induction"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        width={420}
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
            <Button
              type="primary"
              className="btn-dark-green"
              disabled={!pickedWorkerId}
              onClick={startFromPicker}
            >
              Continue
            </Button>
          </div>
        }
      >
        {workerOptions.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No personnel on this contractor yet. Add a worker first, then run their induction."
          />
        ) : (
          <>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Who is this induction for?</div>
            <Select
              style={{ width: "100%" }}
              placeholder="Select a worker"
              value={pickedWorkerId}
              onChange={setPickedWorkerId}
              showSearch
              optionFilterProp="label"
              options={workerOptions.map((w) => ({
                value: w.id,
                label: w.full_name,
              }))}
            />
            <div style={{ marginTop: 8, fontSize: 12, color: "#98a2b3" }}>
              You'll choose the induction next, then complete it here or send
              them a link.
            </div>
          </>
        )}
      </Drawer>

      {/* The same modal the Visitors page uses. It takes a worker instead of a
          visitor here — see RunInductionModal — so the two flows cannot drift. */}
      {supportsInduction && (
        <RunInductionModal
          visible={!!inductionWorker}
          worker={inductionWorker}
          propertyId={propertyId}
          onClose={() => setInductionWorker(null)}
          onCompleted={() => {
            setInductionWorker(null);
            // Refresh so the INDUCTION pill reflects the new status.
            fetchRows();
            onChanged && onChanged();
          }}
        />
      )}

      {/* The same read-only renderer the Visitors page and the Forms page use,
          fed the same shape (see FormModal::showSchemaSubmission), so an
          induction's answers look identical wherever they are opened from. */}
      {inductionResult && (
        <ViewSurveyResultModal
          visibleViewSurveyResult={!!inductionResult}
          cancelViewSurveyResult={() => setInductionResult(null)}
          surveyData={inductionResult}
          type="system_forms"
        />
      )}

      <Drawer
        title={`${editing ? "Edit" : config.addLabel} — ${config.title}`}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={480}
        destroyOnClose
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" className="btn-dark-green" loading={saving} onClick={submit}>
              {editing ? "Save Changes" : "Save"}
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical" onValuesChange={() => setFormTick((t) => t + 1)}>
          {visibleFields.map(renderField)}
        </Form>
      </Drawer>
    </div>
  );
};

export default RegisterTab;
